import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
  Upload,
  LogOut,
  ImageOff,
  ChevronUp,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LogoLockup } from "../components/site/Logo";
import { fileToResizedDataUrl } from "../utils/image";
import SEO from "../components/site/SEO";

export default function AdminDirectors() {
  const { user, loading, logout, authAxios, API } = useAuth();
  const nav = useNavigate();
  const [directors, setDirectors] = useState([]);
  const [editing, setEditing] = useState(null); // director object or "new" or null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login", { replace: true });
  }, [loading, user, nav]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/directors`);
      setDirectors(data);
    } catch {
      toast.error("Failed to load directors");
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await authAxios.delete(`/directors/${id}`);
      toast.success("Removed");
      await load();
    } catch {
      toast.error("Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= directors.length) return;
    const a = directors[idx];
    const b = directors[target];
    setBusy(true);
    try {
      await Promise.all([
        authAxios.put(`/directors/${a.id}`, { order: b.order }),
        authAxios.put(`/directors/${b.id}`, { order: a.order }),
      ]);
      await load();
    } catch {
      toast.error("Failed to reorder");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#050507] grid place-items-center text-[#8B93A5]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      <SEO title="Quasect Admin — Directors" description="Manage director profiles" path="/admin" />
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050507]/85 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/">
              <LogoLockup variant="dark" />
            </Link>
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
              ADMIN CONSOLE
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs text-[#8B93A5] font-mono">
              {user.email}
            </span>
            <button
              onClick={() => {
                logout();
                nav("/admin/login");
              }}
              data-testid="admin-logout"
              className="border border-white/15 hover:border-white text-white px-4 py-2 text-xs uppercase tracking-wider transition-colors flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#39FF14]">
              Leadership / Directors
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">
              Manage directors.
            </h1>
            <p className="text-[#8B93A5] mt-2 max-w-2xl">
              Upload, edit, reorder, and remove leadership profiles shown on the public About
              page. Photos are auto-cropped to square and resized for consistent display.
            </p>
          </div>
          <button
            onClick={() => setEditing("new")}
            data-testid="admin-add-director"
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-5 py-3 text-xs hover:bg-[#32E612] transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Director
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {directors.map((d, idx) => (
            <article
              key={d.id}
              data-testid={`admin-director-${d.id}`}
              className="bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/40 transition-colors overflow-hidden flex flex-col"
            >
              <div className="aspect-square bg-black relative overflow-hidden">
                {d.image_data_url ? (
                  <img
                    src={d.image_data_url}
                    alt={d.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[#5d6373]">
                    <ImageOff className="w-8 h-8" />
                  </div>
                )}
                <div className="absolute top-2 left-2 font-mono text-[9px] tracking-[0.25em] uppercase bg-black/60 px-2 py-1 text-[#00F0FF]">
                  #{idx + 1}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-display font-bold text-lg">{d.name}</div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#00F0FF] mt-1">
                  {d.role}
                </div>
                <p className="text-sm text-[#8B93A5] mt-3 line-clamp-3 leading-relaxed flex-1">
                  {d.bio}
                </p>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap">
                  {d.slug && (
                    <a
                      href={`/card/${d.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-view-card-${d.id}`}
                      className="text-xs uppercase tracking-wider border border-[#00F0FF]/40 text-[#00F0FF] hover:border-[#00F0FF] px-3 py-2 flex items-center gap-1.5"
                    >
                      Card →
                    </a>
                  )}
                  <button
                    onClick={() => setEditing(d)}
                    data-testid={`admin-edit-${d.id}`}
                    className="text-xs uppercase tracking-wider border border-white/15 hover:border-white px-3 py-2 flex items-center gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => remove(d.id, d.name)}
                    disabled={busy}
                    data-testid={`admin-delete-${d.id}`}
                    className="text-xs uppercase tracking-wider border border-white/15 hover:border-[#ff5a5a] hover:text-[#ff5a5a] px-3 py-2 flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                  <div className="ml-auto flex gap-1">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={busy || idx === 0}
                      className="border border-white/15 hover:border-white px-2 py-2 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={busy || idx === directors.length - 1}
                      className="border border-white/15 hover:border-white px-2 py-2 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
          {directors.length === 0 && (
            <div className="col-span-full text-center py-20 border border-dashed border-white/10 text-[#8B93A5]">
              No directors yet. Click <span className="text-white">Add Director</span> to start.
            </div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {editing && (
          <DirectorEditor
            initial={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              setEditing(null);
              await load();
            }}
            authAxios={authAxios}
            nextOrder={directors.length}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function DirectorEditor({ initial, onClose, onSaved, authAxios, nextOrder }) {
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "");
  const [bio, setBio] = useState(initial?.bio || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [linkedin, setLinkedin] = useState(initial?.linkedin || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [imageDataUrl, setImageDataUrl] = useState(initial?.image_data_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB before compression)");
      return;
    }
    setUploading(true);
    try {
      const url = await fileToResizedDataUrl(file, 800, 0.82);
      setImageDataUrl(url);
      toast.success("Image ready");
    } catch (err) {
      toast.error("Couldn't process image");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim() || !role.trim() || !bio.trim()) {
      toast.error("Name, role, and bio are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        role: role.trim(),
        bio: bio.trim(),
        email: email.trim(),
        phone: phone.trim(),
        linkedin: linkedin.trim(),
        slug: slug.trim(),
        image_data_url: imageDataUrl || "",
      };
      if (initial) {
        await authAxios.put(`/directors/${initial.id}`, payload);
      } else {
        await authAxios.post(`/directors`, { ...payload, order: nextOrder });
      }
      toast.success(initial ? "Updated" : "Director added");
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.97, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0D] border border-white/15 w-full max-w-2xl noise relative"
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-white/10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
              {initial ? "EDIT DIRECTOR" : "NEW DIRECTOR"}
            </div>
            <div className="font-display font-bold text-xl mt-1">
              {initial ? initial.name : "Add new"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-editor-close"
            className="w-9 h-9 border border-white/15 hover:border-white grid place-items-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={save} className="p-7 space-y-5">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-32 h-32 bg-black border border-white/15 overflow-hidden shrink-0">
              {imageDataUrl ? (
                <img src={imageDataUrl} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[#5d6373]">
                  <ImageOff className="w-6 h-6" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-[200px] space-y-2">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-white/15 hover:border-white px-4 py-2.5 text-xs uppercase tracking-wider">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "Processing…" : imageDataUrl ? "Replace photo" : "Upload photo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPick}
                  data-testid="admin-upload-image"
                />
              </label>
              {imageDataUrl && (
                <button
                  type="button"
                  onClick={() => setImageDataUrl("")}
                  className="block text-xs text-[#8B93A5] hover:text-[#ff5a5a] underline-offset-2 hover:underline"
                >
                  Remove photo
                </button>
              )}
              <p className="text-[11px] text-[#8B93A5] font-mono">
                Auto cropped to square · max 800px · JPEG
              </p>
            </div>
          </div>

          <Field label="Full Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-director-name"
              className={inputCls}
              placeholder="Sachin Deshpande"
            />
          </Field>
          <Field label="Role / Title">
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="admin-director-role"
              className={inputCls}
              placeholder="Co-Founder"
            />
          </Field>
          <Field label="Bio / Description">
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              data-testid="admin-director-bio"
              rows={5}
              className={`${inputCls} resize-none`}
              placeholder="Short professional bio…"
            />
          </Field>

          <div className="pt-2 mt-2 border-t border-white/10">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-3">
              Virtual Card Contact Info (optional)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-director-email"
                  className={inputCls}
                  placeholder="name@quasect.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="admin-director-phone"
                  className={inputCls}
                  placeholder="+91 98xxxxxxxx"
                />
              </Field>
              <Field label="LinkedIn URL">
                <input
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                  data-testid="admin-director-linkedin"
                  className={inputCls}
                  placeholder="https://www.linkedin.com/in/…"
                />
              </Field>
              <Field label="URL Slug">
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  data-testid="admin-director-slug"
                  className={inputCls}
                  placeholder="auto-generated from name"
                />
              </Field>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving || uploading}
              data-testid="admin-director-save"
              className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3 text-xs hover:bg-[#32E612] transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {initial ? "Save changes" : "Add director"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-white/15 hover:border-white px-6 py-3 text-xs uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 text-white placeholder:text-[#5d6373] px-4 py-3 outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors font-mono text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B93A5] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
